/**
 * List of query parameters that should persist across SPA navigation.
 * These params are merged from the current URL into navigation target URLs.
 */
export const PERSISTENT_QUERY_PARAMS = [
  // GTM/Google Debug
  'gtm_debug',
  'debug',

  // Cache bypass
  'nocache',

  // Google Ads/Analytics
  'gclid',
  'gclsrc',
  '_gl',

  // UTM Campaign Tracking
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
] as const;

/**
 * Merges persistent query params from the current URL into the target URL.
 * Target URL params take precedence if there's a conflict.
 *
 * @param targetUrl - The URL being navigated to
 * @param requestUrl - Optional URL to extract persistent params from (useful for SSR). If not provided, uses window.location.href in browser, or returns targetUrl unchanged in SSR.
 * @returns The target URL with persistent params merged in and with the format of the target URL
 */
export function mergePersistentParams(targetUrl: string, requestUrl?: string): string {
  if (!requestUrl && typeof window === 'undefined') {
    return targetUrl;
  }

  try {
    const currentUrl = new URL(requestUrl || window.location.href);
    const currentParams = new URLSearchParams(currentUrl.search);
    const persistentParamsToMerge: [string, string][] = [];

    for (const param of PERSISTENT_QUERY_PARAMS) {
      const value = currentParams.get(param);
      if (value !== null) {
        persistentParamsToMerge.push([param, value]);
      }
    }

    if (persistentParamsToMerge.length === 0) {
      return targetUrl;
    }

    const isAbsolute = targetUrl.startsWith('http://') || targetUrl.startsWith('https://');
    const url = isAbsolute ? new URL(targetUrl) : new URL(targetUrl, currentUrl.origin);

    for (const [param, value] of persistentParamsToMerge) {
      if (!url.searchParams.has(param)) {
        url.searchParams.set(param, value);
      }
    }

    if (isAbsolute) {
      return url.toString();
    }

    return url.pathname + url.search + url.hash;
  } catch {
    return targetUrl;
  }
}
